import {Checkbox, DatePicker} from 'antd'
import dayjs from 'dayjs'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback} from 'react'
import {IoCalendarOutline} from 'react-icons/io5'
import {NullAsDefault} from 'views/Shared'
import {FilterStatusBar} from "../../../components/Filters/FilterStatusBar";
import _ from "lodash";

// Filter dùng chung cho mọi màn "danh sách timetable" (Lịch sinh hoạt tổng hợp của Workspace, Lịch
// trình của 1 Nhóm/Đơn vị...) - đi kèm với AdvancedFilter của usePaginationHelper. Thanh tìm kiếm
// theo từ khóa dùng search_bar mặc định của usePaginationHelper (bật qua renderHeader), KHÔNG lặp
// lại ở đây - panel này chỉ còn khoảng ngày bắt đầu + "hiện cả lịch đã kết thúc".
//
// `filter.status` là field API thật (1 = chưa hết hạn, undefined = lấy cả đã hết hạn - BE:
// TimetableController@gets/getsByOrganization/getsByClassesOwner/getMilestonesByDepartment) -
// checkbox "include expired" chỉ là UI hiển thị ngược lại của field này, không cần state riêng.

const defaultFilter = {
    status: undefined,
    from_time_open: undefined,
    to_time_open: undefined,
}

export const TimetableScheduleFilter = ({
                                            value: filter = defaultFilter,
                                            onChange: setFilter = NullAsDefault,
                                        }) => {
    const t = useTranslate()
    const includeExpired = filter.status !== 1

    const filteredParams = Object.entries(filter).filter(([key, value]) => {
        switch (key) {
            case "status":
            case "from_time_open":
            case "to_time_open":
            default:
                return value !== undefined && value !== null;
        }
    }).map(([key, value]) => {
        switch (key) {
            case "status":
                return {
                    key,
                    value,
                    displayValue: `${t('status')}: ${t('valid')}`,
                }
            case "from_time_open":
                return {
                    key,
                    value,
                    displayValue: `${t('from time')} ${value}`,
                }
            case "to_time_open":
                return {
                    key,
                    value,
                    displayValue: `${t('to time')} ${value}`,
                }
            default:
                return {
                    key,
                    value,
                    displayValue: value,
                    label: key,
                }
        }
    });

    const handleChange = useCallback(
        (name, value) => {
            setFilter({
                ...filter,
                [name]: value,
            })
        },
        [filter]
    )

    return (
        <div className="mt-3 flex flex-col gap-3">
            <FilterStatusBar
                filteredParams={filteredParams}
                onClear={() => {
                    setFilter(defaultFilter)
                }}
                onRemove={(param) => {
                    handleChange(param, undefined)
                }}
            />
            <div className="flex flex-col md:flex-row gap-3">
                <div className="flex flex-col gap-1">
                    <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">{t('schedule starts from')}</span>
                    <DatePicker
                        allowClear
                        format="DD/MM/YYYY"
                        placeholder={t('schedule starts from')}
                        suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                        value={filter.from_time_open ? dayjs(filter.from_time_open) : undefined}
                        onChange={(_, dateString) => setFilter({...filter, from_time_open: dateString || undefined})}
                        style={{ minWidth: 200 }}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">{t('schedule starts to')}</span>
                    <DatePicker
                        allowClear
                        format="DD/MM/YYYY"
                        placeholder={t('schedule starts to')}
                        suffixIcon={<IoCalendarOutline className="text-color-500"/>}
                        value={filter.to_time_open ? dayjs(filter.to_time_open) : undefined}
                        onChange={(_, dateString) => setFilter({...filter, to_time_open: dateString || undefined})}
                        style={{ minWidth: 200 }}
                    />
                </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                <Checkbox
                    checked={includeExpired}
                    onChange={(e) => setFilter({...filter, status: e.target.checked ? undefined : 1})}
                />
                <span className="text-xs text-color-300 tracking-wider">{t('include expired schedules')}</span>
            </label>
        </div>
    )
}

// query_params chuẩn cho usePaginationHelper - đổi prefix theo từng màn để URL không đụng nhau khi
// nhiều filter cùng xuất hiện trên 1 trang (hiếm nhưng có thể, vd modal mở trong trang có filter khác).
export const createTimetableFilterActions = (prefix) => ({
    sortBy: `${prefix}-sort-by`,
    keyword: `${prefix}-keyword`,
    from_time_open: `${prefix}-from-date`,
    to_time_open: `${prefix}-to-date`,
    include_expired: `${prefix}-include-expired`,
})

// include_expired chỉ tồn tại trên URL/filter UI - quy đổi sang field API thật `status` (1 = chưa
// hết hạn, undefined = lấy cả đã hết hạn) trước khi gọi onFilterChange/đưa vào dependencies.
export const resolveTimetableStatusFilter = (include_expired) => (include_expired ? undefined : 1)
